import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AddressBook, WhatsappLogo, Phone, ArrowsClockwise, Spinner,
  CheckCircle, Plus,
} from "@phosphor-icons/react";

const SCORE_PILL = (score) => {
  if (score >= 80) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (score >= 50) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-slate-50 text-slate-600 border-slate-200";
};

/**
 * MatchPanel — sezione "Clienti compatibili" dentro PropertyDetail.
 * Mostra fino a 30 contatti che matchano l'annuncio, con score e bottone
 * WhatsApp che apre wa.me con messaggio AI personalizzato pre-scritto.
 *
 * Anti-spam by design: 1 conversazione alla volta (deep link wa.me),
 * niente bulk send, tracking "inviato" tramite endpoint separato.
 */
export default function MatchPanel({ propertyId, onOpenSequence }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(data === null);
    setRefreshing(data !== null);
    try {
      const { data: r } = await api.get(`/properties/${propertyId}/matches`);
      setData(r);
    } catch (err) {
      toast.error("Calcolo match fallito");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [propertyId]);

  if (loading) {
    // Mostra uno spinner discreto SOLO al primo load. Se la rubrica è poi
    // vuota nascondiamo del tutto (vedi early-return sotto).
    return null;
  }

  if (!data) return null;
  const matches = data.matches || [];
  const total = data.total_contacts || 0;

  // iter 255: se la rubrica è vuota, NON mostriamo questa sezione.
  // Il prompt "Aggiungi clienti" finiva in cima alla pagina Pianta/Annuncio
  // e confondeva l'utente. L'invito ad aggiungere contatti è già su Contatti.
  if (total === 0) return null;

  return (
    <section className="my-8" data-testid="matches-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <AddressBook size={16} weight="duotone" className="text-[#7C8B6E]" />
            <h2 className="text-[15px] font-semibold text-[#1C1C1A] tracking-tight">
              {matches.length === 1
                ? "1 cliente compatibile"
                : `${matches.length} clienti compatibili`}
            </h2>
          </div>
          <p className="text-[11.5px] text-[#9A968B] mt-0.5">
            Su {total} {total === 1 ? "contatto" : "contatti"} in rubrica · ordinati per affinità
          </p>
        </div>
        <div className="flex items-center gap-2">
          {matches.length > 0 && onOpenSequence && (
            <button
              onClick={onOpenSequence}
              className="text-[11.5px] font-semibold text-white bg-[#25D366] hover:bg-[#1FB855] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors"
              data-testid="matches-send-all"
              title="Manda WhatsApp a tutti i clienti compatibili"
            >
              <WhatsappLogo size={12} weight="fill" /> Manda a tutti
            </button>
          )}
          <button
            onClick={load}
            disabled={refreshing}
            className="text-[11.5px] text-[#7C8B6E] hover:text-[#1C1C1A] inline-flex items-center gap-1 px-2 py-1 disabled:opacity-50"
            data-testid="matches-refresh"
          >
            <ArrowsClockwise size={11} weight="bold" className={refreshing ? "animate-spin" : ""} />
            Ricalcola
          </button>
        </div>
      </div>

      {total === 0 ? (
        <div className="bg-white border border-[#E8E5DE] rounded-xl p-6 text-center">
          <p className="text-[13px] text-[#6B6A65]">
            Nessun contatto in rubrica ancora.
          </p>
          <Link
            to="/dashboard/contatti"
            className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-4 py-2 rounded-md bg-[#1C1C1A] text-white hover:bg-black"
            data-testid="matches-add-contact"
          >
            <Plus size={13} weight="bold" /> Aggiungi clienti
          </Link>
        </div>
      ) : matches.length === 0 ? (
        <div className="bg-white border border-[#E8E5DE] rounded-xl p-6 text-center">
          <p className="text-[13px] text-[#6B6A65]">
            Nessuno dei tuoi {total} contatti corrisponde a questo immobile.
          </p>
          <p className="text-[11.5px] text-[#9A968B] mt-1.5">
            Aggiungi le preferenze ai contatti (tipologia, zona, budget) per avere match più precisi.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="matches-list">
          {matches.map((m) => <MatchRow key={m.contact_id} match={m} propertyId={propertyId} />)}
        </div>
      )}
    </section>
  );
}

function MatchRow({ match: m, propertyId }) {
  const [sent, setSent] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [waLink, setWaLink] = useState(m.wa_link || null);
  const [whatsappMessage, setWhatsappMessage] = useState(m.whatsapp_message || "");

  const ensureMessage = async () => {
    // Se già abbiamo il messaggio (cache locale o pre-caricato), usa quello
    if (whatsappMessage) return { msg: whatsappMessage, link: waLink };
    setGenerating(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/matches/${m.contact_id}/message`);
      const msg = data?.whatsapp_message || "";
      const link = data?.wa_link || null;
      setWhatsappMessage(msg);
      setWaLink(link);
      return { msg, link };
    } catch {
      toast.error("Generazione messaggio fallita");
      return { msg: "", link: null };
    } finally {
      setGenerating(false);
    }
  };

  const handleWa = async () => {
    if (!m.phone) {
      toast.error("Numero di telefono mancante");
      return;
    }
    const { link } = await ensureMessage();
    if (!link) {
      toast.error("Numero di telefono mancante");
      return;
    }
    setSent(true);
    window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="bg-white border border-[#E8E5DE] hover:border-[#7C8B6E] rounded-xl p-4 transition-colors" data-testid={`match-row-${m.contact_id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        {/* Score pill */}
        <div className={`shrink-0 text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-1 border rounded-full ${SCORE_PILL(m.score)}`}>
          {m.score}%
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="font-semibold text-[14px] text-[#1C1C1A] truncate">{m.name}</span>
            {m.status && (
              <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A65] capitalize">· {m.status}</span>
            )}
          </div>
          {m.reasons?.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap text-[11px] text-[#7C8B6E] font-medium">
              {m.reasons.map((r) => (
                <span key={r} className="inline-flex items-center gap-0.5">
                  <CheckCircle size={10} weight="fill" /> {r}
                </span>
              ))}
            </div>
          )}
          {whatsappMessage && (
            <p className="mt-2 text-[12px] text-[#1C1C1A] bg-[#F1F4ED] border-l-2 border-[#7C8B6E] pl-2.5 py-1.5 rounded-r-md italic leading-snug">
              "{whatsappMessage}"
            </p>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          {m.phone && (
            <a
              href={`tel:${m.phone}`}
              className="w-8 h-8 rounded-full bg-[#F5F4F0] hover:bg-[#EAE7DF] text-[#6B6A65] flex items-center justify-center"
              data-testid={`match-call-${m.contact_id}`}
              title={`Chiama ${m.phone}`}
            >
              <Phone size={13} />
            </a>
          )}
          <button
            onClick={handleWa}
            disabled={!m.phone || generating}
            className={`text-[12px] font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors ${
              sent
                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-50"
                : "bg-[#25D366] hover:bg-[#1FB855] text-white"
            } disabled:opacity-50 disabled:bg-slate-100 disabled:text-slate-400`}
            data-testid={`match-wa-${m.contact_id}`}
            title={m.phone ? "Apri WhatsApp con messaggio già scritto" : "Numero mancante"}
          >
            {generating ? (
              <>
                <Spinner size={13} className="animate-spin" /> Scrivo…
              </>
            ) : sent ? (
              <>
                <CheckCircle size={13} weight="fill" /> Aperto
              </>
            ) : (
              <>
                <WhatsappLogo size={13} weight="fill" /> WhatsApp
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
